import { useState, useEffect } from 'react';

const PAYMENT_TABS = [
  { id: 'card',   label: 'Card (Stripe)' },
  { id: 'square', label: 'Square' },
  { id: 'cash',   label: 'Cash' },
  { id: 'check',  label: 'Check' },
  { id: 'zelle',  label: 'Zelle' },
  { id: 'ach',    label: 'ACH' },
];

function fmt(n) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n ?? 0);
}

// ── Stripe Card placeholder form ──────────────────────────────────────────────

function StripeCardForm({ amount, setAmount, note, setNote }) {
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');

  function formatCardNumber(val) {
    const digits = val.replace(/\D/g, '').slice(0, 16);
    return digits.replace(/(.{4})/g, '$1 ').trim();
  }

  function formatExpiry(val) {
    const digits = val.replace(/\D/g, '').slice(0, 4);
    if (digits.length >= 3) return digits.slice(0, 2) + '/' + digits.slice(2);
    return digits;
  }

  return (
    <div className="space-y-4">
      {/* Integration notice */}
      <div className="flex items-start gap-2.5 px-3 py-2.5 rounded-lg"
        style={{ background: 'rgba(46,139,240,0.07)', border: '1px solid rgba(46,139,240,0.2)' }}>
        <svg className="w-4 h-4 flex-shrink-0 mt-0.5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
        </svg>
        <p className="text-[var(--wm-text-ui)] text-blue-700 dark:text-blue-400 leading-relaxed">
          Stripe integration requires API keys in <strong>Settings &rarr; Payments</strong>. Connected when Stripe keys are configured.
        </p>
      </div>

      {/* Amount */}
      <div>
        <label className="block text-[var(--wm-text-ui)] font-semibold text-[#64748B] dark:text-[#7D93AE] mb-1">Amount</label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--wm-text-body)] text-[#64748B] dark:text-[#7D93AE]">$</span>
          <input
            type="number"
            min="0"
            step="0.01"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            className="w-full h-9 pl-7 pr-3 text-[var(--wm-text-body)] rounded-lg border border-gray-200 dark:border-[#364860] bg-gray-50 dark:bg-[#0F1923] text-[#0F1923] dark:text-[#F8FAFE] focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="0.00"
          />
        </div>
      </div>

      {/* Card number */}
      <div>
        <label className="block text-[var(--wm-text-ui)] font-semibold text-[#64748B] dark:text-[#7D93AE] mb-1">Card Number</label>
        <input
          type="text"
          inputMode="numeric"
          value={cardNumber}
          onChange={e => setCardNumber(formatCardNumber(e.target.value))}
          placeholder="1234 5678 9012 3456"
          className="w-full h-9 px-3 text-[var(--wm-text-body)] rounded-lg border border-gray-200 dark:border-[#364860] bg-gray-50 dark:bg-[#0F1923] text-[#0F1923] dark:text-[#F8FAFE] placeholder-gray-400 dark:placeholder-[#4A6380] focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
      </div>

      {/* Expiry + CVV */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[var(--wm-text-ui)] font-semibold text-[#64748B] dark:text-[#7D93AE] mb-1">Expiry</label>
          <input
            type="text"
            inputMode="numeric"
            value={expiry}
            onChange={e => setExpiry(formatExpiry(e.target.value))}
            placeholder="MM/YY"
            className="w-full h-9 px-3 text-[var(--wm-text-body)] rounded-lg border border-gray-200 dark:border-[#364860] bg-gray-50 dark:bg-[#0F1923] text-[#0F1923] dark:text-[#F8FAFE] placeholder-gray-400 dark:placeholder-[#4A6380] focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>
        <div>
          <label className="block text-[var(--wm-text-ui)] font-semibold text-[#64748B] dark:text-[#7D93AE] mb-1">CVV</label>
          <input
            type="text"
            inputMode="numeric"
            maxLength={4}
            value={cvv}
            onChange={e => setCvv(e.target.value.replace(/\D/g, '').slice(0, 4))}
            placeholder="123"
            className="w-full h-9 px-3 text-[var(--wm-text-body)] rounded-lg border border-gray-200 dark:border-[#364860] bg-gray-50 dark:bg-[#0F1923] text-[#0F1923] dark:text-[#F8FAFE] placeholder-gray-400 dark:placeholder-[#4A6380] focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>
      </div>

      {/* Note */}
      <div>
        <label className="block text-[var(--wm-text-ui)] font-semibold text-[#64748B] dark:text-[#7D93AE] mb-1">Note (optional)</label>
        <input
          type="text"
          value={note}
          onChange={e => setNote(e.target.value)}
          placeholder="Internal note..."
          className="w-full h-9 px-3 text-[var(--wm-text-body)] rounded-lg border border-gray-200 dark:border-[#364860] bg-gray-50 dark:bg-[#0F1923] text-[#0F1923] dark:text-[#F8FAFE] placeholder-gray-400 dark:placeholder-[#4A6380] focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
      </div>
    </div>
  );
}

// ── Square placeholder form ───────────────────────────────────────────────────

function SquareForm({ amount, setAmount, note, setNote }) {
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2.5 px-3 py-2.5 rounded-lg"
        style={{ background: 'rgba(46,139,240,0.07)', border: '1px solid rgba(46,139,240,0.2)' }}>
        <svg className="w-4 h-4 flex-shrink-0 mt-0.5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
        </svg>
        <p className="text-[var(--wm-text-ui)] text-blue-700 dark:text-blue-400 leading-relaxed">
          Square integration requires App ID and Location ID in <strong>Settings &rarr; Payments</strong>.
        </p>
      </div>
      <div>
        <label className="block text-[var(--wm-text-ui)] font-semibold text-[#64748B] dark:text-[#7D93AE] mb-1">Amount</label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--wm-text-body)] text-[#64748B] dark:text-[#7D93AE]">$</span>
          <input
            type="number"
            min="0"
            step="0.01"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            className="w-full h-9 pl-7 pr-3 text-[var(--wm-text-body)] rounded-lg border border-gray-200 dark:border-[#364860] bg-gray-50 dark:bg-[#0F1923] text-[#0F1923] dark:text-[#F8FAFE] focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="0.00"
          />
        </div>
      </div>
      <div>
        <label className="block text-[var(--wm-text-ui)] font-semibold text-[#64748B] dark:text-[#7D93AE] mb-1">Note (optional)</label>
        <input
          type="text"
          value={note}
          onChange={e => setNote(e.target.value)}
          placeholder="Internal note..."
          className="w-full h-9 px-3 text-[var(--wm-text-body)] rounded-lg border border-gray-200 dark:border-[#364860] bg-gray-50 dark:bg-[#0F1923] text-[#0F1923] dark:text-[#F8FAFE] placeholder-gray-400 dark:placeholder-[#4A6380] focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
      </div>
    </div>
  );
}

// ── Generic method form (Cash, Check, Zelle, ACH) ─────────────────────────────

function GenericMethodForm({ amount, setAmount, note, setNote }) {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-[var(--wm-text-ui)] font-semibold text-[#64748B] dark:text-[#7D93AE] mb-1">Amount</label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--wm-text-body)] text-[#64748B] dark:text-[#7D93AE]">$</span>
          <input
            type="number"
            min="0"
            step="0.01"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            className="w-full h-9 pl-7 pr-3 text-[var(--wm-text-body)] rounded-lg border border-gray-200 dark:border-[#364860] bg-gray-50 dark:bg-[#0F1923] text-[#0F1923] dark:text-[#F8FAFE] focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="0.00"
          />
        </div>
      </div>
      <div>
        <label className="block text-[var(--wm-text-ui)] font-semibold text-[#64748B] dark:text-[#7D93AE] mb-1">Note (optional)</label>
        <input
          type="text"
          value={note}
          onChange={e => setNote(e.target.value)}
          placeholder="Reference number, check #, etc..."
          className="w-full h-9 px-3 text-[var(--wm-text-body)] rounded-lg border border-gray-200 dark:border-[#364860] bg-gray-50 dark:bg-[#0F1923] text-[#0F1923] dark:text-[#F8FAFE] placeholder-gray-400 dark:placeholder-[#4A6380] focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
      </div>
    </div>
  );
}

// ── Main PaymentModal ─────────────────────────────────────────────────────────

export default function PaymentModal({ invoice, onClose, onPaymentRecorded }) {
  const [activeTab, setActiveTab] = useState('card');
  const [amount, setAmount] = useState(String(invoice?.amountDue ?? invoice?.total ?? ''));
  const [note, setNote] = useState('');

  // Close on Escape
  useEffect(() => {
    function handleKey(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  function handleSubmit(e) {
    e.preventDefault();
    const parsedAmount = parseFloat(amount);
    if (!parsedAmount || parsedAmount <= 0) return;

    const methodMap = {
      card: 'Card',
      square: 'Square',
      cash: 'Cash',
      check: 'Check',
      zelle: 'Zelle',
      ach: 'ACH',
    };

    onPaymentRecorded({
      method: methodMap[activeTab] ?? activeTab,
      amount: parsedAmount,
      note: note.trim(),
      recordedAt: new Date().toISOString(),
    });
    onClose();
  }

  const parsedAmount = parseFloat(amount) || 0;
  const isValid = parsedAmount > 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.5)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-md rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        style={{ background: 'var(--wm-bg-secondary, #fff)', border: '1px solid var(--wm-nav-border, #e2e8f0)', maxHeight: '90vh' }}
      >
        {/* Header */}
        <div className="px-5 py-4 flex items-start gap-3 flex-shrink-0"
          style={{ borderBottom: '1px solid var(--wm-nav-border, #e2e8f0)' }}>
          <div className="flex-1 min-w-0">
            <h2 className="text-[var(--wm-text-body)] font-bold text-[#0F1923] dark:text-[#F8FAFE]">Record Payment</h2>
            {invoice && (
              <p className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE] mt-0.5">
                {invoice.invoiceNumber} &middot; {invoice.customerName}
                {invoice.amountDue != null && (
                  <span className="ml-1.5 font-semibold text-[#0F1923] dark:text-[#F8FAFE]">
                    Due: {fmt(invoice.amountDue)}
                  </span>
                )}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-[#64748B] hover:text-[#0F1923] dark:hover:text-[#F8FAFE] transition-colors flex-shrink-0"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex overflow-x-auto flex-shrink-0 border-b" style={{ borderColor: 'var(--wm-nav-border, #e2e8f0)' }}>
          {PAYMENT_TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-shrink-0 px-3 py-2.5 text-[var(--wm-text-ui)] font-semibold whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'text-[#2E8BF0] border-b-2 border-[#2E8BF0]'
                  : 'text-[#64748B] dark:text-[#7D93AE] hover:text-[#0F1923] dark:hover:text-[#F8FAFE]'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="px-5 py-4">
            {activeTab === 'card' && (
              <StripeCardForm amount={amount} setAmount={setAmount} note={note} setNote={setNote} />
            )}
            {activeTab === 'square' && (
              <SquareForm amount={amount} setAmount={setAmount} note={note} setNote={setNote} />
            )}
            {(activeTab === 'cash' || activeTab === 'check' || activeTab === 'zelle' || activeTab === 'ach') && (
              <GenericMethodForm amount={amount} setAmount={setAmount} note={note} setNote={setNote} />
            )}
          </div>

          {/* Footer */}
          <div className="px-5 pb-5 flex gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 h-10 rounded-xl text-[var(--wm-text-body)] font-medium border border-gray-200 dark:border-[#364860] text-[#64748B] dark:text-[#7D93AE] hover:bg-gray-50 dark:hover:bg-[#243348] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isValid}
              className="flex-1 h-10 rounded-xl text-[var(--wm-text-body)] font-semibold text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ backgroundColor: 'var(--wm-nav-accent, #2E8BF0)' }}
            >
              Record Payment{isValid ? ` — ${fmt(parsedAmount)}` : ''}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
